import { ArrowDownLeft, ArrowUpRight, ChevronRight } from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import useTranslations from "@/shared/lang/useTranslations";

import type { RuntimeWalletTransaction } from "../types/payments-wallets.types";
import type { RecentTransaction } from "../types/wallets-api.types";

type RecentActivityListProps = {
  transactions: RecentTransaction[];
  isLoading: boolean;
  onViewAll?: () => void;
  /** Makes each row clickable, opening its detail in place — no trip to the full statement. */
  onSelect?: (tx: RuntimeWalletTransaction) => void;
};

const formatTimestamp = (ts: string, locale: string): string => {
  if (!ts) return "";
  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) return ts;
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const formatAmount = (amount: number, locale: string): string =>
  new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(Math.abs(amount));

export const RecentActivityList = ({
  transactions,
  isLoading,
  onViewAll,
  onSelect,
}: RecentActivityListProps) => {
  const { t, activeLanguage } = useTranslations("wallets");

  return (
    <div className="flex flex-1 flex-col px-3.5 pb-3.5 pt-3">
      <div className="mb-2 flex items-center justify-between">
        <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          {t("card_recent_activity")}
        </p>
        {onViewAll && (
          <ColocaButton
            variant="ghost"
            size="xs"
            onClick={onViewAll}
            iconTrailing={ChevronRight}
            className="h-6 px-1.5 text-[11px]"
          >
            {t("card_view_all")}
          </ColocaButton>
        )}
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-center gap-2.5 py-1.5">
              <div className="h-5 w-5 animate-pulse rounded bg-muted" />
              <div className="flex-1 space-y-1">
                <div className="h-3 w-3/5 animate-pulse rounded bg-muted" />
                <div className="h-2.5 w-1/3 animate-pulse rounded bg-muted" />
              </div>
              <div className="h-3 w-12 animate-pulse rounded bg-muted" />
            </div>
          ))}
        </div>
      ) : transactions.length === 0 ? (
        <div className="flex flex-1 items-center justify-center">
          <p className="text-center text-xs text-muted-foreground">{t("card_recent_empty")}</p>
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          {transactions.map((tx) => {
            const inbound = tx.amount > 0;
            const isProcessing = (tx.status ?? "").toLowerCase() === "process";
            const clickable = Boolean(onSelect && tx.raw);
            const Row = clickable ? "button" : "div";
            return (
              <Row
                key={tx.id}
                {...(clickable
                  ? {
                      type: "button" as const,
                      onClick: () => onSelect!(tx.raw!),
                    }
                  : {})}
                className={`flex w-full items-center gap-2.5 py-1.5 text-left ${
                  clickable
                    ? "-mx-1 rounded-md px-1 transition-colors hover:bg-muted/40 focus:bg-muted/40 focus:outline-none"
                    : ""
                }`}
              >
                <div
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded border ${
                    isProcessing
                      ? "border-amber-300/50 bg-amber-50 text-amber-600 dark:border-amber-700/40 dark:bg-amber-900/20 dark:text-amber-400"
                      : inbound
                        ? "border-primary/20 bg-primary/10 text-primary"
                        : "border-border bg-muted text-muted-foreground"
                  }`}
                >
                  {inbound ? <ArrowDownLeft size={13} /> : <ArrowUpRight size={13} />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs text-foreground">{tx.title}</p>
                  <div className="flex items-center gap-1.5">
                    <p className="font-mono text-[10px] text-muted-foreground">
                      {formatTimestamp(tx.ts, activeLanguage)}
                    </p>
                    {isProcessing && (
                      <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-wide text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                        <span className="h-1 w-1 animate-pulse rounded-full bg-current" />
                        {t("tx_status_process")}
                      </span>
                    )}
                  </div>
                </div>
                <p
                  className={`shrink-0 font-mono text-xs font-semibold tabular-nums ${
                    isProcessing
                      ? "text-amber-600 dark:text-amber-400"
                      : inbound
                        ? "text-primary"
                        : "text-foreground"
                  }`}
                >
                  {inbound ? "+" : "−"}
                  {formatAmount(tx.amount, activeLanguage)}
                </p>
                {clickable && (
                  <ChevronRight size={14} className="shrink-0 text-muted-foreground" aria-hidden="true" />
                )}
              </Row>
            );
          })}
        </div>
      )}
    </div>
  );
};
